import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import Calories from 'components/desktop/Ingredient/Search/Components/SearchResultListRow/Calories';
import Density from 'components/desktop/Ingredient/Search/Components/SearchResultListRow/Density';
import Picture from 'components/desktop/Ingredient/Search/Components/SearchResultListRow/Picture';
import BtnGroup, { BtnData } from 'components/desktop/_general/Button/BtnGroup/BtnGroup';
import FormatedDate from 'components/desktop/_general/FormatedDate/FormatedDate';
import IconComponent from 'components/desktop/_general/Icon/IconComponent';
import IconCo2Round from 'components/desktop/_general/IconCo2/IconCo2Round';
import NutriScoreDiagram from 'components/desktop/_general/NutriScoreDiagram/NutriScoreDiagram';
import SeasonalityBar from 'components/desktop/_general/SeasonalityBar/SeasonalityBar';
import getSeasonalityCode from 'functions/getSeasonalityCode';
import usePreferredAllergens from 'hooks/redux/usePreferredAllergens';
import * as Icon from 'types/Icon/Icon';
import * as Ingredient from 'types/Ingredient/Search/Ingredient';

interface IProps {
	ingredient: Ingredient.Type;
	handleRestoreIngredient: (input: string) => Promise<void>;
}

const ArchiveResultListRow = (props: IProps) => {
	const { t } = useTranslation();
	const preferredAllergens = usePreferredAllergens();

	const allergensUserSet = useMemo(
		() => preferredAllergens.toSet((x) => `${x.id}_${x.logic}`),
		[preferredAllergens]
	);

	const getButtonDataRestore = (): BtnData => {
		return {
			onClick: () => props.handleRestoreIngredient(props.ingredient.id),
		};
	};

	const renderButtons = (): JSX.Element => {
		return <BtnGroup restore={getButtonDataRestore()} />;
	};

	return (
		<div className="recipe-table-item" key={props.ingredient.nameSingularTranslation}>
			<Picture image={props.ingredient.image} />
			<div id="column-first">
				<div id="title">
					<strong>{props.ingredient.nameSingularTranslation}</strong>
				</div>
				<div id="info">
					<Calories
						calories={props.ingredient.calories}
						characteristic={props.ingredient.characteristic}
					/>
					<Density density={props.ingredient.density} />
				</div>
			</div>
			<div id="column-second">
				<div className="icon-column justify-content-start">
					<SeasonalityBar
						seasonalityCode={getSeasonalityCode(props.ingredient.seasons)}
					/>
				</div>
				<div className="row">
					<div className="col-5">
						<span className="grey">{t('_general:LAST_CHANGE_DATE')}:</span>
						<br />
						<span className="grey">{t('_general:LAST_EDITOR')}:</span>
						<br />
					</div>
					<div className="col-7">
						<FormatedDate date={props.ingredient.lastEditedDateUtc} />
						<br />
						<span>{Ingredient.getAuthor(props.ingredient)}</span>
					</div>
				</div>
			</div>
			<div id="column-last">
				{props.ingredient.hasNutriScore && props.ingredient.nutriScore && (
					<div className="justify-content-end">
						<NutriScoreDiagram nutriScore={props.ingredient.nutriScore} />
					</div>
				)}
				<div id="icon-rows">
					{Ingredient.getIcons(props.ingredient, allergensUserSet)
						.slice(0, 4)
						.map((icon: Icon.Type, i: number) => (
							<IconComponent icon={icon} key={i} />
						))}
					<IconCo2Round
						color={
							props.ingredient.carbonDioxideLabel
								? props.ingredient.carbonDioxideLabel.color!
								: ''
						}
						label={
							props.ingredient.carbonDioxideLabel
								? props.ingredient.carbonDioxideLabel.label!
								: ''
						}
					/>
				</div>
				<div>{renderButtons()}</div>
			</div>
		</div>
	);
};

export default ArchiveResultListRow;
